import { styled } from 'baseui'
import { FlexCenter, FlexCenterY } from '@/shared/theme/styled-components'

const radius = value => ({
  borderTopLeftRadius: value,
  borderTopRightRadius: value,
  borderBottomRightRadius: value,
  borderBottomLeftRadius: value,
})

const border = (width, color) => ({
  borderTopWidth: width,
  borderBottomWidth: width,
  borderLeftWidth: width,
  borderRightWidth: width,
  borderTopStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderRightStyle: 'solid',
  borderTopColor: color,
  borderBottomColor: color,
  borderLeftColor: color,
  borderRightColor: color,
})

export const ModalOverrides = {
  Dialog: {
    style: () => ({
      borderRadius: '2rem',
      overflow: 'hidden',
    }),
  },
}

export const PulseModalOverrides = {
  Dialog: {
    style: ({ $theme }) => ({
      width: '90vw',
      maxWidth: '520px',
      ...radius('12px'),
      backgroundColor: $theme.colors.white,
      boxShadow: $theme.lighting.shadowPulseModal,
    }),
  },
}

export const HeaderContainer = styled(FlexCenterY, ({ $pulse }) =>
  $pulse
    ? { gap: '10px' }
    : {
        padding: '0.5rem 1rem',
        alignItems: 'flex-start',
      },
)

export const StyledIcon = styled('img', ({ $pulse }) =>
  $pulse
    ? { width: '18px', height: '18px' }
    : {
        marginRight: '0.45rem',
        width: '1.5rem',
        height: '1.5rem',
      },
)

export const TitleText = styled('h3', ({ $theme, $pulse }) =>
  $pulse
    ? {
        margin: 0,
        fontSize: '18px',
        lineHeight: '24px',
        fontWeight: 600,
        color: $theme.colors.textPulseStrong,
      }
    : {
        marginTop: 0,
        marginBottom: 0,
        marginRight: 0,
        marginLeft: '0.5rem',
        fontSize: '1.2rem',
        fontWeight: '600',
        color: $theme.colors.textHeading,
      },
)

export const OptionsContainer = styled('div', ({ $pulse }) =>
  $pulse
    ? { display: 'flex', gap: '12px' }
    : {
        display: 'flex',
        justifyContent: 'space-around',
        padding: '0.25rem 0',
      },
)

const pulseOptionCardStyle = ($theme, $selected) => ({
  flexGrow: 1,
  flexBasis: 0,
  height: '112px',
  flexDirection: 'column',
  gap: '10px',
  cursor: 'pointer',
  boxSizing: 'border-box',
  ...radius('10px'),
  ...border('1px', $selected ? $theme.colors.primaryPulse : $theme.colors.borderPulse),
  backgroundColor: $selected ? $theme.colors.bgPulseSelected : $theme.colors.white,
  boxShadow: $selected ? $theme.lighting.shadowPulseFocus : 'none',
  color: $selected ? $theme.colors.primaryPulse : $theme.colors.textPulseMuted,
  transitionProperty: 'border-color, background-color, color',
  transitionDuration: '150ms',
  ':hover': {
    ...border('1px', $selected ? $theme.colors.primaryPulse : $theme.colors.borderPulseHover),
    color: $theme.colors.primaryPulse,
  },
})

const optionCardHighlight = $theme => ({
  border: `0.25rem solid ${$theme.colors.primaryAlt}`,
  '--icon-color': $theme.colors.primary400,
})

export const OptionCardStyled = styled(FlexCenter, ({ $theme, $pulse, $selected }) =>
  $pulse
    ? pulseOptionCardStyle($theme, $selected)
    : {
        width: '8rem',
        height: '7.5rem',
        border: `0.1rem solid ${$theme.colors.borderSoft}`,
        borderRadius: '0.5rem',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'border-color 0.3s ease',
        ':hover': optionCardHighlight($theme),
        ...($selected ? optionCardHighlight($theme) : {}),
      },
)

export const OptionIconWrapper = styled(FlexCenter, ({ $theme, $pulse }) =>
  $pulse
    ? { width: '32px', height: '32px', color: 'currentColor' }
    : {
        width: '2.8rem',
        height: '2.8rem',
        marginBottom: '0.85rem',
        color: `var(--icon-color, ${$theme.colors.iconMuted})`,
        transition: 'color 0.3s ease',
        '& svg': {
          width: '100%',
          height: '100%',
          fill: 'currentColor',
        },
      },
)

export const OptionText = styled('span', ({ $theme, $pulse, $selected }) =>
  $pulse
    ? {
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: 600,
        color: $selected ? $theme.colors.primaryPulse : $theme.colors.textPulseStrong,
      }
    : {
        fontSize: '1.2rem',
        color: $theme.colors.textHeading,
      },
)

export const ButtonsContainer = styled('div', ({ $theme, $pulse }) =>
  $pulse
    ? {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
        marginTop: '20px',
        paddingTop: '16px',
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: $theme.colors.borderPulse,
      }
    : {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '0.75rem',
      },
)

export const CancelButtonOverrides = {
  BaseButton: {
    style: ({ $theme }) => ({
      width: 'calc(50% - 0.5rem)',
      backgroundColor: $theme.colors.white,
      color: $theme.colors.errorInput,
      border: `0.1rem solid ${$theme.colors.errorInput}`,
      borderRadius: '1rem',
      ':hover': {
        backgroundColor: $theme.colors.errorInput,
        color: $theme.colors.white,
      },
    }),
  },
}

export const ConfirmButtonOverrides = {
  BaseButton: {
    style: ({ $theme }) => ({
      width: 'calc(50% - 0.5rem)',
      backgroundColor: $theme.colors.white,
      color: $theme.colors.primary400,
      border: `0.1rem solid ${$theme.colors.primaryAlt}`,
      borderRadius: '1rem',
      ':hover': {
        backgroundColor: $theme.colors.primary400,
        color: $theme.colors.white,
      },
    }),
  },
}
